import store from '@/store';
import analytics from '@/utils/analytics';
import { auth0, isTokenExpiredError, retrieveAccessToken } from '@/utils/auth0';
import Cookies from 'js-cookie';
import { fetchUserData } from '@/store/actions';
import { bausRequest } from '@/utils/baus-request';

import {
  RouteNames,
  RouteName,
  TypedRouter,
  TypedRouteRecordRaw,
} from '@/data/routes';
import { PAYMENTS_TRACKING_PARAMS } from '@/data/payments';
import { LocalStorage, LocalStorageKeys } from '@/utils/local-storage';
import { isBackendDevelopment, ENV } from '../../config/app-env';
import { RouteLocation, useRouter as vueUseRouter } from 'vue-router';
import { UserResponseDto } from '@clearbanc/baus-sdk';
import { authGuard } from '@auth0/auth0-vue';
import { isArray } from 'lodash';
import { setupStatsigClient } from '@/utils/init-statsig';

function getAttributionParams(path: string): any {
  const params = new URLSearchParams(path.split('?')[1]);
  const filteredParams: any = {};

  for (const key of params.keys()) {
    if (PAYMENTS_TRACKING_PARAMS.includes(key)) {
      filteredParams[key] = params.get(key);
    }
  }

  return filteredParams;
}

export function initializePageStorage(
  to: RouteLocation,
  from: RouteLocation,
): void {
  // store tracking params
  const { query, name } = to;
  if (
    Object.keys(query).some((param) => PAYMENTS_TRACKING_PARAMS.includes(param))
  ) {
    LocalStorage.setItem(LocalStorageKeys.SIGNUP_PARAMS, query);

    if (query.amplitude_id) {
      LocalStorage.setItem(LocalStorageKeys.AMPLITUDE_ID, query.amplitude_id);
    }
  }

  const isUserGettingAuthenticated =
    to.name === RouteNames.AUTH.LOGIN && !!query.code;

  if (isUserGettingAuthenticated) {
    LocalStorage.removeItem(
      LocalStorageKeys.DISMISS_REVENUE_ACCOUNT_MODAL_THIS_SESSION,
    );
  }

  const isShowLegacyModal =
    (to.meta.isLegacy && !from.meta.isLegacy) ||
    (to.path === from.path && store.getters.showLegacyModal);

  store.dispatch('setShowLegacyModal', isShowLegacyModal);

  // store shopify oauth params
  const currentShopifyOauthParams = LocalStorage.getItem(
    LocalStorageKeys.SHOPIFY_OAUTH_PAYLOAD,
  );

  if (name === RouteNames.OAUTH.SIGNUP && !currentShopifyOauthParams) {
    LocalStorage.setItem(LocalStorageKeys.SHOPIFY_OAUTH_PAYLOAD, query);
  }
}

export function isAuthenticatedPartner(): boolean {
  return (
    auth0.isAuthenticated.value && auth0.user.value?.userType === 'partner'
  );
}

export async function fetchGlobalFeatureFlags(): Promise<void> {
  try {
    if (!store.getters.globalFeatureFlags) {
      await store.dispatchApiAction('FETCH_GLOBAL_FEATURE_FLAGS');
      if (!store.getters.globalFeatureFlags) {
        await store.commit('DEFAULT_GLOBAL_FEATURE_FLAGS');
      }
    }
    // eslint-disable-next-line no-empty
  } catch (_error) {}
}

export async function fetchUserFeatureFlags(): Promise<void> {
  const { featureFlagsToFetch, getAllEnabledExperiments, user } = store.getters;
  if (featureFlagsToFetch.length) {
    await store.dispatchApiAction('FETCH_USER_FEATURE_FLAGS');

    const { id, email } = user;
    // Should add global feature flags?
    analytics.identify(id, {
      enabledExperiments: getAllEnabledExperiments,
      email,
    });
  }
}

export async function syncDevEnvironmentUser(to: RouteLocation): Promise<void> {
  if (
    isBackendDevelopment &&
    !!auth0.user?.value?.userId &&
    !ENV.PROXY_TO_STAGING &&
    [RouteNames.HOME.INDEX, RouteNames.OAUTH.SIGNUP].includes(to.name as any) // Users are redirected to `oauth-signup` when installing the Shopify Native App
  ) {
    await bausRequest.post('/users/local-sync');
  }
}

export async function initializeStatsigClient(): Promise<void> {
  const { userId, email } = auth0.user?.value ?? {};
  if (userId) {
    const business = store.getters.business;
    const statsigUser = {
      userID: userId.toString(),
      email,
      custom: {
        businessCreatedAt: business?.createdAt,
      },
    };
    await setupStatsigClient(statsigUser);
  }
}

export async function processUTMParams(
  to: RouteLocation,
  from: RouteLocation,
): Promise<boolean> {
  const filteredToParams = getAttributionParams(to.fullPath);
  const filteredFromParams = getAttributionParams(from.fullPath);

  let paramsToSend = {};
  const toHasParams = Object.keys(filteredToParams).length > 0;
  const fromHasParams = Object.keys(filteredFromParams).length > 0;

  if (toHasParams) {
    paramsToSend = filteredToParams;
  } else if (fromHasParams) {
    paramsToSend = filteredFromParams;
  }

  const { userId, loginsCount } = auth0.user?.value ?? {};

  const hasParamsToSend = !!Object.keys(paramsToSend).length;

  if (hasParamsToSend) {
    const authType = loginsCount === 1 ? 'signup' : 'login';

    if (userId) {
      // Stores UTM data in DB
      store.dispatchApiAction('STORE_USER_ATTRIBUTION', {
        urlParams: paramsToSend,
        userId,
        authType,
      });
    } else {
      // Sends UTM data to Auth0 via BAUS
      store.dispatchApiAction('SEND_UTM_DATA', {
        urlParams: paramsToSend,
        authType,
      });
    }
  }
  return hasParamsToSend;
}

export function removeUTMParams(to: RouteLocation): RouteLocation | undefined {
  const hasParamsToRemove = PAYMENTS_TRACKING_PARAMS.some(
    (param) => to.query[param],
  );
  if (hasParamsToRemove) {
    const modifiedTo = {
      ...to,
      query: { ...to.query },
    };

    PAYMENTS_TRACKING_PARAMS.forEach((param) => {
      delete modifiedTo.query[param];
    });
    return modifiedTo;
  }
  return undefined;
}

async function getRedirectName(to: RouteLocation): Promise<string | undefined> {
  if (store.getters.isMaintenancePageEnabled) {
    return RouteNames.MAINTENANCE.INDEX;
  }

  if (to.query.error) {
    return RouteNames.AUTH.ERROR;
  }

  try {
    await retrieveAccessToken();
  } catch (_error) {
    if (isTokenExpiredError(auth0.error)) {
      return RouteNames.AUTH.ERROR;
    }
  }

  const isUserPendingEmailVerification =
    auth0.isAuthenticated.value && !auth0.user?.value?.userId;
  if (to.name !== RouteNames.AUTH.LOGOUT && isUserPendingEmailVerification) {
    return RouteNames.EMAIL_VERIFICATION.INDEX;
  }
  return undefined;
}

export async function checkForRedirect(
  to: RouteLocation,
): Promise<string | undefined> {
  const redirectName = await getRedirectName(to);
  if (redirectName && redirectName !== to.name) {
    return redirectName;
  }
  return undefined;
}

export function isPermittedRoute(to: RouteLocation): boolean {
  const isAuthGuarded = to.matched.some((record) =>
    isArray(record.beforeEnter)
      ? record.beforeEnter.some((beforeEnter) => beforeEnter === authGuard)
      : record.beforeEnter === authGuard,
  );
  return !isAuthGuarded || auth0.isAuthenticated.value;
}

export function isAuthRoute(to: RouteLocation) {
  return Object.values(RouteNames.AUTH).includes(to.name as any);
}

export function trackPageNavigation(
  to: RouteLocation,
  from: RouteLocation,
): void {
  const pageProps = {
    ...(to.redirectedFrom && {
      redirected_from: to.redirectedFrom,
    }),
    productSegment: store.getters.productSegmentLabel,
    fullPath: to.fullPath,
    path: to.path,
  };
  analytics.page(to.name as string, pageProps);
}

export async function fetchUser(): Promise<UserResponseDto> {
  const { userId, email } = auth0.user?.value ?? {};
  if (userId?.toString() !== store.getters.userId) {
    const user = await fetchUserData(store, userId);
    Cookies.set('cb-user-email', email as string, { path: '/' });
    return user;
  }
  return store.getters.user;
}

export function checkForIncompleteOnboardingStep(
  to: RouteLocation,
): string | undefined {
  const isOnboardingPage = Object.values(RouteNames.ONBOARDING).some(
    (value) => {
      if (typeof value === 'object') {
        return Object.values(value).includes(to.name as any);
      }
      return value === to.name;
    },
  );

  const { name } = store.getters.firstIncompleteOnboardingStep ?? {};
  const isOnboardingCompleted = name === RouteNames.HOME.INITIAL;
  const isOauthSignupPage = to.name === RouteNames.OAUTH.SIGNUP;
  const shopifyPending = LocalStorage.getItem(
    LocalStorageKeys.SHOPIFY_OAUTH_PAYLOAD,
  );

  const shouldRedirect =
    !isOnboardingPage &&
    !isOnboardingCompleted &&
    !isOauthSignupPage &&
    !shopifyPending &&
    name !== to.name;

  if (shouldRedirect) {
    LocalStorage.setItem(LocalStorageKeys.NEW_USER, true);
    return name;
  }
  return undefined;
}

export function createRoutes(...routes: TypedRouteRecordRaw[]) {
  return routes;
}

export function useRouter(): TypedRouter {
  const router = vueUseRouter();

  const typedRouter: TypedRouter = {
    push: (to) => router.push(to as any),
    replace: (to) => router.replace(to as any),
    back: () => router.back(),
    go: (delta) => router.go(delta),
    currentRoute: router.currentRoute,
  };

  return typedRouter;
}

export function getOnboardingCompletionRoute(
  businessProfileSteps: Record<string, { isComplete: boolean }>,
): RouteName {
  const incompleteSteps = Object.keys(businessProfileSteps).some(
    (step) => !businessProfileSteps[step].isComplete,
  );

  return incompleteSteps ? RouteNames.PROFILE.INDEX : RouteNames.HOME.INITIAL;
}

export function flattenRouteNames(obj: any): string[] {
  let result: string[] = [];
  if (typeof obj === 'string') {
    result.push(obj);
  } else if (Array.isArray(obj)) {
    for (const item of obj) {
      result = result.concat(flattenRouteNames(item));
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const key in obj) {
      result = result.concat(flattenRouteNames(obj[key]));
    }
  }
  return result;
}
